<div *ngIf="isDrawerOpen()" class="fixed top-0 right-0 max-h-screen w-[640px] bg-base-300 overflow-y-auto z-50">
  <!-- Close Panel Button - Top Right -->
  <button
    (click)="closeDrawer()"
    class="absolute top-4 right-4 bg-base-300 text-base-content px-4 py-2 rounded font-medium transition duration-150 ease-in-out flex items-center z-10"
  >
    <fa-icon [icon]="'angles-right'" class="mr-2"></fa-icon>
    <span class="text-sm font-medium">Close Panel</span>
  </button>

  <div class="p-3 sm:p-4">
    <div class="max-w-4xl mx-auto bg-base-300">
      <!-- Header -->
      <div class="p-3 border-b border-base-300">
        <!-- Title -->
        <div class="flex items-center pr-32">
          <!-- Master File Icon -->
          <div
            class="w-7 h-7 mr-2 bg-primary"
            style="
              mask: url('/icons/master-file.svg') no-repeat center / contain;
              -webkit-mask: url('/icons/master-file.svg') no-repeat center / contain;
            "
          ></div>
          <h1 class="text-2xl font-bold">
            {{ (analyticalDataset() ? analyticalDataset()!.gcs_file_path : selectedSequence()?.gcs_file_path) || '' }}
          </h1>
        </div>
      </div>

      <form *ngIf="formReady()" [formGroup]="sequenceMetadataForm" (ngSubmit)="onSave()">
        <div class="p-4">
          <!-- Alert Banner -->
          <div
            *ngIf="showAlert() && !isReadOnly()"
            class="mb-4 relative flex items-start justify-between px-4 py-3 rounded text-gray-900"
            [ngStyle]="{
              'background-color':
                alertMessage().type === 'warning'
                  ? '#ffeaca'
                  : alertMessage().type === 'success'
                    ? '#d4f8d6'
                    : '#ffd9e0',
              'border-left':
                alertMessage().type === 'warning'
                  ? '30px solid #fc8802'
                  : alertMessage().type === 'success'
                    ? '30px solid #2ad6a2'
                    : '30px solid #f75b99',
            }"
          >
            <div class="flex-1 pr-8">
              <p class="text-sm font-medium">
                {{ alertMessage().message }}
              </p>

              <!-- Promotion validation issue summary -->
              <ng-container *ngIf="promotionIssues() as issues">
                <ul
                  *ngIf="
                    issues.errors.length > 0 ||
                    issues.warnings.length > 0 ||
                    issues.missingRequiredKeys.length > 0
                  "
                  class="mt-2 list-disc pl-5 text-xs space-y-1"
                >
                  <li
                    *ngFor="let key of issues.missingRequiredKeys"
                    class="text-base-content"
                  >
                    <span class="font-semibold">Required:</span> {{ key }} is required.
                  </li>
                  <li
                    *ngFor="let issue of issues.errors"
                    class="text-base-content"
                  >
                    <span class="font-semibold">{{ issue.code }}:</span> {{ issue.message }}
                  </li>
                  <li
                    *ngFor="let issue of issues.warnings"
                    class="text-base-content"
                  >
                    <span class="font-semibold">Warning ({{ issue.code }}):</span> {{ issue.message }}
                  </li>
                </ul>
              </ng-container>
            </div>
          </div>

          <!-- Status Section -->
          <div class="mb-4 flex items-center justify-between gap-3">
            <div class="flex items-center gap-3">
              <span class="text-sm font-medium">Status</span>
              <span
                class="inline-flex items-center px-4 py-2 text-sm font-medium rounded"
                [ngStyle]="{
                  'background-color': isPendingArchive() ? '#f4b740' : (isPublicStatus() ? '#2ad6a2' : '#f75b99'),
                }"
              >
                {{ displayStatus() }}
              </span>
            </div>
            <!-- Pagination Arrows -->
            <div *ngIf="shouldShowPagination()" class="flex items-center gap-2">
              <button
                type="button"
                (click)="onPrevSequence()"
                [disabled]="drawerCurrentIndex() === 0"
                class="btn btn-sm btn-outline"
                [class.btn-disabled]="drawerCurrentIndex() === 0"
              >
                <fa-icon [icon]="'chevron-left'"></fa-icon>
              </button>
              <span class="text-sm text-base-content/70">
                {{ drawerCurrentIndex() + 1 }} / {{ drawerAllSequenceIds().length }}
              </span>
              <button
                type="button"
                (click)="onNextSequence()"
                [disabled]="drawerCurrentIndex() === drawerAllSequenceIds().length - 1"
                class="btn btn-sm btn-outline"
                [class.btn-disabled]="drawerCurrentIndex() === drawerAllSequenceIds().length - 1"
              >
                <fa-icon [icon]="'chevron-right'"></fa-icon>
              </button>
            </div>
          </div>

          <!-- External Storage Location (shown for ARCHIVED status) -->
          <div
            *ngIf="analyticalDataset()?.status === 'ARCHIVED' || selectedSequence()?.status === 'ARCHIVED'"
            class="mb-4"
          >
            <span class="text-sm font-medium text-base-content/70">External Storage Location</span>
            <p class="text-sm mt-1">
              {{
                analyticalDataset()?.external_storage_location || selectedSequence()?.external_storage_location || 'N/A'
              }}
            </p>
          </div>

          <!-- Deletion Reason (shown for DELETED status) -->
          <div
            *ngIf="analyticalDataset()?.status === 'DELETED' || selectedSequence()?.status === 'DELETED'"
            class="mb-4"
          >
            <span class="text-sm font-medium text-base-content/70">Deletion Reason</span>
            <p class="text-sm mt-1">
              {{ analyticalDataset()?.deletion_reason || selectedSequence()?.deletion_reason || 'N/A' }}
            </p>
          </div>
          <!-- Copied To Section -->
          <div
            *ngIf="
              !analyticalDataset() &&
              meUser()?.is_platform_admin &&
              isPublicStatus() &&
              (selectedSequence()?.copied_to_datasets?.length ?? 0) > 0
            "
            class="mb-2"
          >
            <div class="flex justify-between items-center mb-2">
              <h2 class="text-xl font-bold">Copied To</h2>
              <button
                type="button"
                (click)="toggleCopiedToSection()"
                class="cursor-pointer hover:text-base-content/60 transition-colors"
                [attr.aria-label]="isCopiedToExpanded() ? 'Collapse section' : 'Expand section'"
              >
                <fa-icon
                  [icon]="isCopiedToExpanded() ? 'compress-arrows-alt' : 'expand-arrows-alt'"
                  class="h-5 w-5"
                ></fa-icon>
              </button>
            </div>
            <div class="border-t border-base-300 mb-3"></div>

            <div *ngIf="isCopiedToExpanded()" class="overflow-x-auto">
              <table class="table table-sm w-full">
                <thead class="text-sm">
                  <tr>
                    <th class="p-2">Dataset</th>
                    <th class="p-2">Lab</th>
                    <th class="p-2">Project</th>
                    <th class="p-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let item of selectedSequence()?.copied_to_datasets" class="hover:bg-base-200">
                    <td class="p-2 text-sm">{{ item.analytical_dataset_name }}</td>
                    <td class="p-2 text-sm">{{ item.lab_name }}</td>
                    <td class="p-2 text-sm">{{ item.project_name }}</td>
                    <td class="p-2 text-right">
                      <fa-icon
                        [icon]="['far', 'eye']"
                        class="cursor-pointer text-primary hover:text-primary-focus"
                        (click)="onViewDataset(item)"
                        title="View Project Details"
                      ></fa-icon>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Sequence Details Section -->
          <div class="flex justify-between items-center mb-2">
            <h2 class="text-xl font-bold">Sequence Details</h2>
            <button
              type="button"
              (click)="toggleSequenceDetailsSection()"
              class="cursor-pointer hover:text-base-content/60 transition-colors"
              [attr.aria-label]="isSequenceDetailsExpanded() ? 'Collapse section' : 'Expand section'"
            >
              <fa-icon
                [icon]="isSequenceDetailsExpanded() ? 'compress-arrows-alt' : 'expand-arrows-alt'"
                class="h-5 w-5"
              ></fa-icon>
            </button>
          </div>
          <!-- Divider -->
          <div class="border-t border-base-300 mb-3"></div>

          <!-- Read-only view for non-DRAFT status -->
          <div *ngIf="isReadOnly() && isSequenceDetailsExpanded()" class="space-y-3">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
              <label class="text-sm font-medium col-span-1 pr-2"
                >Sequence ID<span class="text-error text-lg font-bold">*</span>:</label
              >
              <div class="col-span-2 text-sm">
                {{ (analyticalDataset() ? analyticalDataset()!.id : selectedSequence()?.id) || '-' }}
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
              <label class="text-sm font-medium col-span-1 pr-2"
                >Sequence filename<span class="text-error text-lg font-bold">*</span>:</label
              >
              <div class="col-span-2 text-sm">
                {{
                  (analyticalDataset() ? analyticalDataset()!.gcs_file_path : selectedSequence()?.gcs_file_path) || '-'
                }}
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
              <label class="text-sm font-medium col-span-1 pr-2">Description:</label>
              <div class="col-span-2 text-sm">{{ sequenceMetadataForm.get('description')?.value || '-' }}</div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
              <label class="text-sm font-medium col-span-1 pr-2">Lab:</label>
              <div class="col-span-2 text-sm">
                {{ (analyticalDataset() ? analyticalDataset()!.lab_name : selectedSequence()?.lab_name) || '-' }}
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
              <label class="text-sm font-medium col-span-1 pr-2">Organization:</label>
              <div class="col-span-2 text-sm">{{ organization || '-' }}</div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
              <label class="text-sm font-medium col-span-1 pr-2">Created on:</label>
              <div class="col-span-2 text-sm">
                {{
                  (analyticalDataset() ? analyticalDataset()!.created_at : selectedSequence()?.created_at)
                    | date: 'MM/dd/yyyy'
                }}
              </div>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
              <label class="text-sm font-medium col-span-1 pr-2">Retention Requirement Expires On:</label>
              <div class="col-span-2 text-sm">
                {{
                  addYears(analyticalDataset() ? analyticalDataset()!.created_at : selectedSequence()?.created_at)
                    | date: 'MM/dd/yyyy'
                }}
              </div>
            </div>
          </div>

          <!-- Editable view for DRAFT status -->
          <div *ngIf="!isReadOnly() && isSequenceDetailsExpanded()" class="space-y-3">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
              <label for="sequence-id" class="text-sm font-medium col-span-1 pr-2"
                >Sequence ID:<span class="text-error text-lg font-bold">*</span></label
              >
              <input
                type="text"
                id="sequence-id"
                [value]="selectedSequence()?.id || ''"
                readonly
                class="col-span-2 p-2 border border-base-300 bg-base-100 rounded text-base-content/70 cursor-not-allowed"
              />
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
              <div class="col-span-1 pr-2">
                <label for="sequence-filename" class="text-sm font-medium"
                  >Sequence filename:<span class="text-error text-lg font-bold">*</span></label
                >
                <p class="text-xs text-base-content/70 mt-1">Do not change the file extension.</p>
              </div>
              <div class="col-span-2">
                <input
                  type="text"
                  id="sequence-filename"
                  formControlName="gcs_file_path"
                  class="w-full p-2 border rounded text-base-content placeholder:text-base-content/60 focus:ring-blue-500 focus:border-blue-500"
                  [ngClass]="{
                    'border-red-500 bg-red-50': sequenceMetadataForm.get('gcs_file_path')?.errors,
                    'border-base-300 bg-base-100': !sequenceMetadataForm.get('gcs_file_path')?.errors,
                  }"
                />
                <p
                  *ngIf="sequenceMetadataForm.get('gcs_file_path')?.errors?.['serverError']"
                  class="text-red-500 text-xs mt-1"
                >
                  {{ sequenceMetadataForm.get('gcs_file_path')?.errors?.['serverError'] }}
                </p>
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
              <label for="lab-sequence-description" class="text-sm font-medium pt-2 col-span-1">Description:</label>
              <textarea
                id="lab-sequence-description"
                rows="4"
                formControlName="description"
                autocomplete="off"
                name="description-{{ selectedSequence()?.id || 'new' }}"
                class="col-span-2 p-2 border border-base-300 rounded bg-base-100 text-base-content/70 placeholder:text-base-content/60 focus:ring-blue-500 focus:border-blue-500 resize-y"
              ></textarea>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
              <span class="text-sm font-medium">Lab:</span>
              <div class="col-span-2 text-sm">{{ selectedSequence()?.lab_name || '' }}</div>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
              <span class="text-sm font-medium">Organization:</span>
              <div class="col-span-2 text-sm">{{ organization }}</div>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
              <span class="text-sm font-medium">Created on:</span>
              <div class="col-span-2 text-sm">{{ selectedSequence()?.created_at | date: 'MM/dd/yyyy' }}</div>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
              <span class="text-sm font-medium">Retention Requirement Expires On:</span>
              <div class="col-span-2 text-sm">{{ addYears(selectedSequence()?.created_at) | date: 'MM/dd/yyyy' }}</div>
            </div>
          </div>

          <!-- Metadata Template Section -->
          <div class="flex justify-between items-center mt-6 mb-2">
            <h2 class="text-xl font-bold">Metadata Template</h2>
          </div>
          <!-- Divider -->
          <div class="border-t border-base-300 mb-3"></div>
          <div class="space-y-3">
            <!-- Read-only view for non-DRAFT status -->
            <div *ngIf="isReadOnly()" class="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
              <label class="text-sm font-medium col-span-1 pr-2">Source Type template:</label>
              <div class="col-span-2 text-sm">{{ getSourceTypeName() }}</div>
            </div>

            <!-- Editable view for DRAFT status -->
            <ng-container *ngIf="!isReadOnly()">
              <div class="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
                <label for="source-type" class="text-sm font-medium col-span-1 pr-2"
                  ><span class="text-error text-lg font-bold">*</span>Select a Source Type template
                  <p class="text-xs mb-1.5">
                    You must select a source type to begin applying metadata to your sequence file.
                  </p></label
                >
                <div class="col-span-2">
                  <ng-select
                    [items]="sourceTypes"
                    bindLabel="name"
                    bindValue="id"
                    [searchable]="true"
                    [clearable]="true"
                    formControlName="source_type"
                    class="bg-base-100 border p-2 text-base-content/70 border-base-300 rounded-md focus:border-blue-500 focus:ring-blue-500 w-full"
                    (change)="onSourceTypeChange($event)"
                    placeholder="Please Select"
                  >
                  </ng-select>
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
                <div class="col-span-1">
                  <h3 class="text-sm font-medium">Apply metadata via CSV</h3>
                  <p class="text-xs mt-1">Get started by uploading a formatted CSV</p>
                </div>
                <div class="col-span-2 relative group">
                  <a
                    (click)="onUploadCSV()"
                    class="text-blue-400 text-sm font-medium hover:text-blue-300 underline transition duration-150 ease-in-out"
                  >
                    Upload a CSV
                  </a>
                  <!-- Tooltip -->
                  <div
                    *ngIf="alertMessage().type === 'default'"
                    class="absolute left-0 bottom-full mb-2 hidden group-hover:block bg-base-300 text-base-content text-xs p-2 rounded shadow-lg w-64"
                  >
                    Upload a preformatted CSV to apply metadata settings
                  </div>
                </div>
              </div>
            </ng-container>
          </div>

          <!-- Metadata Section & other sections -->
          <ng-container *ngFor="let section of formSections; trackBy: trackBySectionTitle">
            <ng-container>
              <div class="flex justify-between items-center mt-6 mb-2">
                <h2 class="text-xl font-bold">{{ section.title }}</h2>
                <div class="flex items-center gap-4">
                  <a
                    *ngIf="section.sectionKey === 'core_fields' && canRedact()"
                    (click)="onRedactMetadata()"
                    [title]="
                      selectedSequence()?.status === 'PRIMARY'
                        ? 'Open the metadata editor to correct or redact (hide from other labs) baseline values'
                        : 'Open the metadata editor to redact (hide from other labs) baseline values'
                    "
                    [class.pointer-events-none]="preparingCorrection()"
                    [class.opacity-60]="preparingCorrection()"
                    class="flex items-center gap-1.5 text-blue-400 text-sm font-medium hover:text-blue-300 underline cursor-pointer transition duration-150 ease-in-out"
                  >
                    <ng-container *ngIf="preparingCorrection(); else redactIdle">
                      <span class="loading loading-spinner loading-xs"></span>
                      Opening editor...
                    </ng-container>
                    <ng-template #redactIdle>
                      <fa-icon [icon]="'eye-slash'"></fa-icon>
                      {{
                        selectedSequence()?.status === 'PRIMARY'
                          ? 'Correct or redact metadata'
                          : 'Redact metadata (open editor)'
                      }}
                    </ng-template>
                  </a>
                  <button
                    *ngIf="section.sectionKey === 'sourceType_fields'"
                    type="button"
                    (click)="toggleSourceTypeSection()"
                    class="cursor-pointer hover:text-base-content/60 transition-colors"
                    [attr.aria-label]="isSourceTypeExpanded() ? 'Collapse section' : 'Expand section'"
                  >
                    <fa-icon
                      [icon]="isSourceTypeExpanded() ? 'compress-arrows-alt' : 'expand-arrows-alt'"
                      class="h-5 w-5"
                    ></fa-icon>
                  </button>
                </div>
              </div>
              <!-- Divider -->
              <div class="border-t border-base-300 mb-3"></div>
              <div
                *ngIf="section.sectionKey === 'sourceType_fields' && isSourceTypeExpanded()"
                class="mb-3 grid grid-cols-1 md:grid-cols-3 gap-3 items-center"
              >
                <label class="text-sm font-medium col-span-1 pr-2"
                  >Source Type <span class="text-error text-lg font-bold">*</span></label
                >
                <div class="col-span-2 text-sm">{{ getSourceTypeName() }}</div>
              </div>

              <!-- Read-only view for non-DRAFT status -->
              <div
                *ngIf="isReadOnly() && (section.sectionKey !== 'sourceType_fields' || isSourceTypeExpanded())"
                class="space-y-3"
              >
                <ng-container *ngFor="let field of getFields(section.sectionKey)">
                  <div class="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
                    <label class="text-sm font-medium col-span-1 pr-2">
                      {{ field.key }}<span *ngIf="field.is_required" class="text-error text-lg font-bold">*</span>:
                    </label>
                    <div class="col-span-2 text-sm flex items-center gap-2">
                      <span class="flex-1">
                        <ng-container *ngIf="field.data_type === 'DATE'; else regularValue">
                          {{ formatDateForDisplay(field.value) }}
                        </ng-container>
                        <ng-template #regularValue>{{ field.value || '-' }}</ng-template>
                      </span>
                      <span
                        *ngIf="field.corrections.length > 0"
                        class="inline-flex items-center px-2 py-0 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800 border border-amber-300 cursor-help"
                        (mouseenter)="showCorrectionTooltip($event, field.corrections)"
                        (mouseleave)="hideCorrectionTooltip()"
                      >
                        corrected
                      </span>
                      <span
                        *ngIf="field.is_redacted"
                        class="inline-flex items-center px-2 py-0 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800 border border-amber-300 cursor-help"
                        (mouseenter)="showRedactionTooltip($event)"
                        (mouseleave)="hideRedactionTooltip()"
                      >
                        redacted
                      </span>
                    </div>
                  </div>
                </ng-container>
              </div>

              <!-- Editable view for DRAFT status -->
              <div
                *ngIf="!isReadOnly() && (section.sectionKey !== 'sourceType_fields' || isSourceTypeExpanded())"
                class="space-y-3"
                [formGroup]="section.form"
              >
                <ng-container *ngFor="let key of section.keys; trackBy: trackById">
                  <!-- TEXT Field -->
                  <div *ngIf="key.data_type === 'TEXT'" class="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
                    <label [for]="'field-' + key.id" class="text-sm font-medium col-span-1 pr-2 pt-2">
                      {{ key.key_name || key.name
                      }}<span *ngIf="key.is_required" class="text-error text-lg font-bold">*</span>
                      <app-requirement-hint
                        *ngIf="getFieldSchemaForKey(key) as fs"
                        [field]="fs"
                        [crossFieldRules]="crossFieldRules"
                        compact
                      />
                    </label>
                    <div class="col-span-2">
                      <input
                        [id]="'field-' + key.id"
                        [formControlName]="key.name"
                        type="text"
                        class="w-full p-2 border rounded bg-base-100 text-base-content/70 placeholder:text-base-content/60 focus:ring-blue-500 focus:border-blue-500"
                        [class.border-error]="getFieldControl(section.sectionKey, key.name)?.invalid"
                        [class.border-base-300]="!getFieldControl(section.sectionKey, key.name)?.invalid"
                        [placeholder]="'Enter ' + (key.key_name || key.name)"
                      />
                      <p
                        *ngIf="getFieldServerError(section.sectionKey, key.name) as serverError"
                        class="text-error text-xs mt-1"
                      >
                        {{ serverError }}
                      </p>
                    </div>
                  </div>

                  <!-- GPS Field -->
                  <div *ngIf="key.data_type === 'GPS'" class="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
                    <div class="col-span-1 pr-2">
                      <label [for]="'field-' + key.id" class="text-sm font-medium block">
                        {{ key.key_name || key.name
                        }}<span *ngIf="key.is_required" class="text-error text-lg font-bold">*</span>
                        <app-requirement-hint
                          *ngIf="getFieldSchemaForKey(key) as fs"
                          [field]="fs"
                          [crossFieldRules]="crossFieldRules"
                          compact
                        />
                      </label>
                      <p class="text-xs text-base-content/70 mt-1">Decimal degrees, ≥4 decimal places<br />e.g. 38.9800 N 77.1100 W</p>
                    </div>
                    <div class="col-span-2">
                      <input
                        [id]="'field-' + key.id"
                        [formControlName]="key.name"
                        type="text"
                        class="w-full p-2 border rounded bg-base-100 text-base-content/70 placeholder:text-base-content/60 focus:ring-blue-500 focus:border-blue-500"
                        [class.border-error]="getFieldControl(section.sectionKey, key.name)?.invalid && getFieldControl(section.sectionKey, key.name)?.touched"
                        [class.border-success]="getFieldControl(section.sectionKey, key.name)?.valid && getFieldControl(section.sectionKey, key.name)?.touched && getFieldControl(section.sectionKey, key.name)?.value"
                        [class.border-base-300]="!getFieldControl(section.sectionKey, key.name)?.touched"
                        placeholder="e.g. 38.9800 N 77.1100 W"
                      />
                      <p
                        *ngIf="getFieldControl(section.sectionKey, key.name)?.hasError('pattern') && getFieldControl(section.sectionKey, key.name)?.touched"
                        class="text-error text-xs mt-1"
                      >
                        Invalid GPS format. Use decimal degrees with ≥4 decimal places — latitude followed by N or S, longitude followed by E or W. e.g. 38.9800 N 77.1100 W
                      </p>
                      <p
                        *ngIf="getFieldServerError(section.sectionKey, key.name) as serverError"
                        class="text-error text-xs mt-1"
                      >
                        {{ serverError }}
                      </p>
                    </div>
                  </div>

                  <!-- NUMBER Field -->
                  <div *ngIf="key.data_type === 'NUMBER'" class="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
                    <label [for]="'field-' + key.id" class="text-sm font-medium col-span-1 pr-2 pt-2">
                      {{ key.key_name || key.name
                      }}<span *ngIf="key.is_required" class="text-error text-lg font-bold">*</span>
                      <app-requirement-hint
                        *ngIf="getFieldSchemaForKey(key) as fs"
                        [field]="fs"
                        [crossFieldRules]="crossFieldRules"
                        compact
                      />
                    </label>
                    <div class="col-span-2">
                      <input
                        [id]="'field-' + key.id"
                        [formControlName]="key.name"
                        type="number"
                        class="w-full p-2 border rounded bg-base-100 text-base-content/70 placeholder:text-base-content/60 focus:ring-blue-500 focus:border-blue-500"
                        [class.border-error]="getFieldControl(section.sectionKey, key.name)?.invalid"
                        [class.border-base-300]="!getFieldControl(section.sectionKey, key.name)?.invalid"
                        [placeholder]="'Enter ' + (key.key_name || key.name)"
                      />
                      <p
                        *ngIf="getFieldServerError(section.sectionKey, key.name) as serverError"
                        class="text-error text-xs mt-1"
                      >
                        {{ serverError }}
                      </p>
                    </div>
                  </div>

                  <!-- DATE Field -->
                  <div *ngIf="key.data_type === 'DATE'" class="grid grid-cols-1 md:grid-cols-3 gap-3 items-center">
                    <label [for]="'field-' + key.id" class="text-sm font-medium col-span-1 pr-2">
                      {{ key.key_name || key.name
                      }}<span *ngIf="key.is_required" class="text-error text-lg font-bold">*</span>
                      <app-requirement-hint
                        *ngIf="getFieldSchemaForKey(key) as fs"
                        [field]="fs"
                        [crossFieldRules]="crossFieldRules"
                        compact
                      />
                    </label>
                    <div class="col-span-2 relative">
                      <input
                        [id]="'field-' + key.id"
                        [formControlName]="key.name"
                        type="date"
                        class="w-full p-2 border rounded bg-base-100 text-base-content/70 focus:ring-blue-500 focus:border-blue-500"
                        [class.border-error]="getFieldControl(section.sectionKey, key.name)?.invalid"
                        [class.border-base-300]="!getFieldControl(section.sectionKey, key.name)?.invalid"
                      />
                      <p
                        *ngIf="getFieldServerError(section.sectionKey, key.name) as serverError"
                        class="text-error text-xs mt-1"
                      >
                        {{ serverError }}
                      </p>
                      <!-- <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-base-content/70 absolute right-2.5 top-1/2 transform -translate-y-1/2 pointer-events-none"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg> -->
                    </div>
                  </div>

                  <!-- SELECT Field (Single) -->
                  <div
                    *ngIf="key.data_type === 'SELECT' && !key.multiple_select"
                    class="grid grid-cols-1 md:grid-cols-3 gap-3 items-start"
                  >
                    <label [for]="'field-' + key.id" class="text-sm font-medium col-span-1 pr-2 pt-2">
                      {{ key.key_name || key.name
                      }}<span *ngIf="key.is_required" class="text-error text-lg font-bold">*</span>
                      <app-requirement-hint
                        *ngIf="getFieldSchemaForKey(key) as fs"
                        [field]="fs"
                        [crossFieldRules]="crossFieldRules"
                        compact
                      />
                    </label>
                    <div class="col-span-2">
                      <ng-select
                        [id]="'field-' + key.id"
                        [items]="key.options"
                        bindLabel="name"
                        bindValue="value"
                        [clearable]="true"
                        [searchable]="true"
                        placeholder="Please Select"
                        class="bg-base-100 text-base-content/70 border p-2 rounded-md focus:border-blue-500 focus:ring-blue-500 w-full"
                        [formControlName]="key.name"
                        [ngClass]="{
                          'border-error': getFieldControl(section.sectionKey, key.name)?.invalid,
                          'border-base-300': !getFieldControl(section.sectionKey, key.name)?.invalid,
                        }"
                      >
                        <ng-template ng-footer-tmp>
                          <button type="button"
                                  class="block w-full text-left text-[13px] text-info hover:bg-base-200 border-t border-base-300 rounded-b-md px-2 py-2"
                                  (mousedown)="$event.preventDefault(); openSuggestOption(key)">
                            Don't see what you need? Request a new option...
                          </button>
                        </ng-template>
                      </ng-select>
                      <div *ngIf="pendingValuesForKey(key) as pendingValues" class="mt-1 flex flex-wrap items-center gap-1">
                        <span *ngFor="let v of pendingValues"
                              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-900 border border-amber-300"
                              title="Awaiting admin review. File stays in Draft until reviewed.">
                          <span class="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                          {{ v }} (pending)
                        </span>
                        <a *ngIf="pendingValues.length"
                           routerLink="/my-metadata-requests"
                           class="inline-flex items-center gap-1 text-[11px] text-info hover:underline whitespace-nowrap">
                          My requests
                          <fa-icon [icon]="'arrow-up-right-from-square'" class="text-[9px]"></fa-icon>
                        </a>
                      </div>
                      <p
                        *ngIf="getFieldServerError(section.sectionKey, key.name) as serverError"
                        class="text-error text-xs mt-1"
                      >
                        {{ serverError }}
                      </p>
                    </div>
                  </div>

                  <!-- LIST Field (free-form multi-value) -->
                  <div
                    *ngIf="key.data_type === 'LIST'"
                    class="grid grid-cols-1 md:grid-cols-3 gap-3 items-start"
                  >
                    <div class="col-span-1 pr-2">
                      <label [for]="'field-' + key.id" class="text-sm font-medium block">
                        {{ key.key_name || key.name
                        }}<span *ngIf="key.is_required" class="text-error text-lg font-bold">*</span>
                        <app-requirement-hint
                          *ngIf="getFieldSchemaForKey(key) as fs"
                          [field]="fs"
                          [crossFieldRules]="crossFieldRules"
                          compact
                        />
                      </label>
                      <p class="text-xs mt-1">Type a value and press Enter or click Add</p>
                    </div>
                    <div class="col-span-2">
                      <div class="flex gap-2">
                        <input
                          #listInput
                          [id]="'field-' + key.id"
                          type="text"
                          placeholder="Add a value..."
                          class="input input-bordered flex-1 h-10 rounded border-base-300 text-sm focus:outline-none focus:border-base-300"
                          (keydown.enter)="$event.preventDefault(); addListValue(section.sectionKey, key.name, listInput)"
                        />
                        <button
                          type="button"
                          class="h-10 px-3 rounded border border-base-300 text-sm hover:bg-base-200 transition-colors"
                          (click)="addListValue(section.sectionKey, key.name, listInput)"
                        >
                          Add
                        </button>
                      </div>
                      <!-- Display added values as chips -->
                      <div *ngIf="getFieldArray(section.sectionKey, key.name)?.length" class="mt-2 flex flex-wrap gap-1">
                        <div
                          *ngFor="let control of getFieldArray(section.sectionKey, key.name)?.controls; let i = index"
                          class="flex items-center gap-1 px-2 py-1 rounded-full text-sm border border-base-300 bg-base-100"
                        >
                          <span>{{ control.value }}</span>
                          <button
                            type="button"
                            (click)="removeListValue(section.sectionKey, key.name, i)"
                            class="text-red-400 hover:text-red-300 leading-none"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                      <p
                        *ngIf="getFieldServerError(section.sectionKey, key.name) as serverError"
                        class="text-error text-xs mt-1"
                      >
                        {{ serverError }}
                      </p>
                    </div>
                  </div>

                  <!-- SELECT Field (Multiple) or MULTISELECT -->
                  <div
                    *ngIf="(key.data_type === 'SELECT' && key.multiple_select) || key.data_type === 'MULTISELECT'"
                    class="grid grid-cols-1 md:grid-cols-3 gap-3 items-start"
                  >
                    <div class="col-span-1 pr-2">
                      <label [for]="'field-' + key.id" class="text-sm font-medium block">
                        {{ key.key_name || key.name
                        }}<span *ngIf="key.is_required" class="text-error text-lg font-bold">*</span>
                        <app-requirement-hint
                          *ngIf="getFieldSchemaForKey(key) as fs"
                          [field]="fs"
                          [crossFieldRules]="crossFieldRules"
                          compact
                        />
                      </label>
                      <p class="text-xs mt-1">Hold Shift key to select multiple</p>
                    </div>
                    <div class="col-span-2">
                      <ng-select
                        [id]="'field-' + key.id"
                        [items]="key.options"
                        bindLabel="name"
                        bindValue="value"
                        [multiple]="true"
                        [searchable]="true"
                        [clearable]="true"
                        placeholder="Select one or more"
                        class="bg-base-100 text-base-content/70 border p-2 rounded-md focus:border-blue-500 focus:ring-blue-500 w-full"
                        [ngClass]="{
                          'border-error': getFieldArray(section.sectionKey, key.name)?.invalid,
                          'border-base-300': !getFieldArray(section.sectionKey, key.name)?.invalid,
                        }"
                        [hideSelected]="true"
                        [ngModel]="getFieldArray(section.sectionKey, key.name)?.value || []"
                        [ngModelOptions]="{ standalone: true }"
                        (ngModelChange)="onMultiSelectChange($event, section.sectionKey, key.name)"
                      >
                        <ng-template ng-label-tmp let-item="item">
                          <!-- Empty template to hide tags inside input -->
                        </ng-template>
                        <ng-template ng-footer-tmp>
                          <button type="button"
                                  class="block w-full text-left text-[13px] text-info hover:bg-base-200 border-t border-base-300 rounded-b-md px-2 py-2"
                                  (mousedown)="$event.preventDefault(); openSuggestOption(key)">
                            Don't see what you need? Request a new option...
                          </button>
                        </ng-template>
                      </ng-select>
                      <div *ngIf="pendingValuesForKey(key) as pendingValues" class="mt-1 flex flex-wrap items-center gap-1">
                        <span *ngFor="let v of pendingValues"
                              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-900 border border-amber-300"
                              title="Awaiting admin review. File stays in Draft until reviewed.">
                          <span class="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                          {{ v }} (pending)
                        </span>
                        <a *ngIf="pendingValues.length"
                           routerLink="/my-metadata-requests"
                           class="inline-flex items-center gap-1 text-[11px] text-info hover:underline whitespace-nowrap">
                          My requests
                          <fa-icon [icon]="'arrow-up-right-from-square'" class="text-[9px]"></fa-icon>
                        </a>
                      </div>

                      <!-- Display selected values -->
                      <div *ngIf="getFieldArray(section.sectionKey, key.name)?.length" class="mt-2 space-y-1">
                        <div
                          *ngFor="let control of getFieldArray(section.sectionKey, key.name)?.controls; let i = index"
                          class="flex items-center justify-between px-2 py-1 rounded text-sm border border-base-300 bg-base-100"
                        >
                          <span>{{ control.value }}</span>
                          <button
                            type="button"
                            (click)="removeMultiSelectValue(section.sectionKey, key.name, i)"
                            class="text-red-400 hover:text-red-300"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                      <p
                        *ngIf="getFieldServerError(section.sectionKey, key.name) as serverError"
                        class="text-error text-xs mt-1"
                      >
                        {{ serverError }}
                      </p>
                    </div>
                  </div>
                </ng-container>
              </div>
            </ng-container>
          </ng-container>
          <div class="mb-4"></div>

          <!-- Post-Analysis Metadata (PRIMARY files only) -->
          <app-post-analysis-section
            *ngIf="isPublicStatus() && postAnalysisFileId() as paFileId"
            [fileId]="paFileId"
            [sourceTypeId]="postAnalysisSourceTypeId()"
            [isPrimary]="isPublicStatus()"
            [editable]="!analyticalDataset() && !postAnalysisReadOnly()"
          ></app-post-analysis-section>

          <!-- Action Buttons -->
          <div *ngIf="!isReadOnly()" class="flex justify-end gap-3 mt-6 mb-4">
            <button
              type="submit"
              [disabled]="metadataLoading()"
              class="px-6 py-2 border border-base-300 rounded bg-transparent hover:bg-base-300 transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ metadataLoading() ? 'Saving...' : 'Save' }}
            </button>
            <button
              type="button"
              class="px-4 py-2 rounded-md font-medium text-white bg-info border border-info hover:bg-info transition-colors duration-200 disabled:bg-transparent disabled:border-base-300 disabled:text-base-content/70 disabled:opacity-50 disabled:cursor-not-allowed"
              [disabled]="sequenceMetadataForm.invalid"
              (click)="promoteToPrimary()"
            >
              Promote To Primary
            </button>
          </div>

          <div *ngIf="isReadOnly()" class="flex justify-end gap-3 mt-6 mb-4">
            <button
              type="button"
              (click)="onExportCSV()"
              class="px-6 py-2 border border-base-300 rounded bg-transparent hover:bg-base-300 transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Export as CSV
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</div>

<div
  *ngIf="hoverCorrection() as ttip"
  class="fixed z-[60] w-80 max-w-[90vw] -translate-y-1/2 rounded bg-neutral p-3 text-xs text-neutral-content shadow-lg pointer-events-none"
  [style.top.px]="ttip.top"
  [style.right.px]="ttip.right"
>
  <p class="font-semibold mb-2">Correction history</p>
  <ul class="space-y-2">
    <li *ngFor="let c of ttip.corrections" class="border-t border-neutral-content/20 pt-2 first:border-0 first:pt-0">
      <div class="mb-1">
        <span class="line-through opacity-70">{{ (c.old || []).join(', ') || '(none)' }}</span>
        &rarr;
        <span class="font-medium">{{ (c.new || []).join(', ') || '(none)' }}</span>
      </div>
      <div class="opacity-80">{{ c.corrected_by_name || 'Unknown' }} &middot; {{ c.corrected_at | date: 'MM/dd/yyyy' }}</div>
      <div class="opacity-80">Reason: {{ c.reason }}</div>
    </li>
  </ul>
</div>

<div
  *ngIf="hoverRedaction() as ttip"
  class="fixed z-[60] w-80 max-w-[90vw] -translate-y-1/2 rounded bg-neutral p-3 text-xs text-neutral-content shadow-lg pointer-events-none"
  [style.top.px]="ttip.top"
  [style.right.px]="ttip.right"
>
  <p class="font-semibold mb-2">Redacted field</p>
  <p class="opacity-80">
    This value is hidden from other labs in the data catalogue, CSV exports, and search. It stays visible to your
    lab and users with approved file access through datasets.
  </p>
</div>

<!-- Unsaved Changes Confirmation Modal -->
<app-unsaved-changes-confirmation-modal
  (saveAndClose)="onSaveAndClose()"
  (closeWithoutSaving)="onCloseWithoutSaving()"
  (continueEditing)="onContinueEditing()"
></app-unsaved-changes-confirmation-modal>

<app-suggest-option-modal
  *ngIf="suggestModalFileIds.length > 0 && suggestModalTemplateId != null"
  [fileIds]="suggestModalFileIds"
  [templateId]="suggestModalTemplateId"
  [keyName]="suggestModalKeyName"></app-suggest-option-modal>
